import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { findGeomNode } from "../../utils/geomSelection";
import { useSyncMobileEdgeChromeFromMenuOpen } from "../../hooks/useSyncMobileEdgeChromeFromMenuOpen";

/**
 * Owns the scene-graph right-click context menu: the "what is the menu
 * targeting?" target ids, the open/position state, every submenu open flag,
 * the action callbacks that open/close the menu (one per node type), and the
 * outside-click + Escape-key effects that dismiss it.
 *
 * Why a single hook for so much surface? The menu is a small state machine —
 * any opener clears every other entity field, resets submenus, and sets one
 * target plus the position. Splitting state from openers would force a long
 * setter list across the boundary; keeping them together lets each opener
 * stay a tight imperative reset.
 *
 * Inputs:
 *   - bodies: scene body map, used to derive geom-collision/visual labelling
 *   - isPhysicsRunning: empty-space "Add new" submenu must stay closed when
 *     authoring is locked by a running sim
 *   - interactionLayerRef: DOM ref to the SVG interaction layer; the openers
 *     translate raw client coords into layer-local coords for menu positioning
 *   - effectiveBatch* arrays + Sets from useSceneGraphMultiSelection: when the
 *     right-clicked target is part of the current multi-selection, the menu
 *     operates on the whole batch; otherwise it operates on just the target.
 */
export function useSceneGraphContextMenu({
  bodies,
  isPhysicsRunning,
  interactionLayerRef,
  effectiveBatchBodyUuids,
  effectiveBatchBodyUuidSet,
  effectiveBatchMeshIds,
  effectiveBatchMeshIdSet,
  effectiveBatchGeomKeys,
  effectiveBatchGeomKeySet,
  effectiveBatchSiteIds,
  effectiveBatchSiteIdSet,
  effectiveBatchInertialKeys,
  effectiveBatchInertialKeySet,
  effectiveBatchJointIds,
  effectiveBatchJointIdSet,
}) {
  const [contextMenuType, setContextMenuType] = useState("body");
  const [contextMenuBodyUuid, setContextMenuBodyUuid] = useState(null);
  const [contextMenuMeshId, setContextMenuMeshId] = useState(null);
  const [contextMenuGeomKey, setContextMenuGeomKey] = useState(null);
  const [contextMenuSiteId, setContextMenuSiteId] = useState(null);
  const [contextMenuInertialKey, setContextMenuInertialKey] = useState(null);
  const [contextMenuJointId, setContextMenuJointId] = useState(null);
  const [contextMenuCameraId, setContextMenuCameraId] = useState(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [reparentSubmenuOpen, setReparentSubmenuOpen] = useState(false);
  const [deleteSubmenuOpen, setDeleteSubmenuOpen] = useState(false);
  const [addNewSubmenuOpen, setAddNewSubmenuOpen] = useState(false);
  const [duplicateSubmenuOpen, setDuplicateSubmenuOpen] = useState(false);
  const [addJointKindSubmenuOpen, setAddJointKindSubmenuOpen] = useState(false);
  const [addGeomKindSubmenuOpen, setAddGeomKindSubmenuOpen] = useState(false);
  const [addVisualGeomKindSubmenuOpen, setAddVisualGeomKindSubmenuOpen] = useState(false);

  const contextMenuRef = useRef(null);

  // ── derived target ids ─────────────────────────────────────────────────
  // Each contextMenu*Ids memo: when the right-clicked target is itself in the
  // current multi-selection, the menu acts on the whole batch; otherwise it
  // acts on just the explicit target. This keeps right-click on a single
  // unselected node from accidentally affecting unrelated multi-selection.
  const contextMenuBodyUuids = useMemo(() => {
    if (contextMenuBodyUuid) {
      return effectiveBatchBodyUuidSet.has(contextMenuBodyUuid)
        ? effectiveBatchBodyUuids
        : [contextMenuBodyUuid];
    }
    return effectiveBatchBodyUuids;
  }, [contextMenuBodyUuid, effectiveBatchBodyUuidSet, effectiveBatchBodyUuids]);
  const contextMenuBodyUuidSet = useMemo(
    () => new Set(contextMenuBodyUuids),
    [contextMenuBodyUuids]
  );
  const contextMenuMeshIds = useMemo(() => {
    if (contextMenuMeshId) {
      return effectiveBatchMeshIdSet.has(contextMenuMeshId)
        ? effectiveBatchMeshIds
        : [contextMenuMeshId];
    }
    return effectiveBatchMeshIds;
  }, [contextMenuMeshId, effectiveBatchMeshIdSet, effectiveBatchMeshIds]);
  const contextMenuGeomKeys = useMemo(() => {
    if (contextMenuGeomKey) {
      return effectiveBatchGeomKeySet.has(contextMenuGeomKey)
        ? effectiveBatchGeomKeys
        : [contextMenuGeomKey];
    }
    return effectiveBatchGeomKeys;
  }, [contextMenuGeomKey, effectiveBatchGeomKeySet, effectiveBatchGeomKeys]);
  const contextMenuDirectCollisionGeomKey = useMemo(() => {
    if (!contextMenuGeomKey) return null;
    const resolved = findGeomNode(bodies, contextMenuGeomKey);
    return resolved?.geom?.visualOnly === true ? null : contextMenuGeomKey;
  }, [bodies, contextMenuGeomKey]);
  const contextMenuGeomSelectionLabel = useMemo(() => {
    if (contextMenuGeomKeys.length === 0) return "geoms";
    let visualCount = 0;
    let collisionCount = 0;
    contextMenuGeomKeys.forEach((geomKey) => {
      const resolved = findGeomNode(bodies, geomKey);
      if (resolved?.geom?.visualOnly === true) {
        visualCount += 1;
      } else {
        collisionCount += 1;
      }
    });
    if (visualCount > 0 && collisionCount === 0) {
      return contextMenuGeomKeys.length === 1 ? "visual" : "visuals";
    }
    if (collisionCount > 0 && visualCount === 0) {
      return contextMenuGeomKeys.length === 1 ? "collision" : "collisions";
    }
    return contextMenuGeomKeys.length === 1 ? "geom" : "geoms";
  }, [bodies, contextMenuGeomKeys]);
  const contextMenuSiteIds = useMemo(() => {
    if (contextMenuSiteId) {
      return effectiveBatchSiteIdSet.has(contextMenuSiteId)
        ? effectiveBatchSiteIds
        : [contextMenuSiteId];
    }
    return effectiveBatchSiteIds;
  }, [contextMenuSiteId, effectiveBatchSiteIdSet, effectiveBatchSiteIds]);
  const contextMenuInertialKeys = useMemo(() => {
    if (contextMenuInertialKey) {
      return effectiveBatchInertialKeySet.has(contextMenuInertialKey)
        ? effectiveBatchInertialKeys
        : [contextMenuInertialKey];
    }
    return effectiveBatchInertialKeys;
  }, [contextMenuInertialKey, effectiveBatchInertialKeySet, effectiveBatchInertialKeys]);
  const contextMenuInertialSourceBodyUuids = useMemo(() => {
    const source = new Set();
    contextMenuInertialKeys.forEach((key) => {
      const str = String(key || "");
      const sep = str.indexOf(":");
      const bodyUuid = sep > 0 ? str.slice(0, sep) : null;
      if (bodyUuid) source.add(bodyUuid);
    });
    return source;
  }, [contextMenuInertialKeys]);
  const contextMenuJointIds = useMemo(() => {
    if (contextMenuJointId) {
      return effectiveBatchJointIdSet.has(contextMenuJointId)
        ? effectiveBatchJointIds
        : [contextMenuJointId];
    }
    return effectiveBatchJointIds;
  }, [contextMenuJointId, effectiveBatchJointIdSet, effectiveBatchJointIds]);
  const contextMenuCameraIds = useMemo(
    () => (contextMenuCameraId ? [contextMenuCameraId] : []),
    [contextMenuCameraId]
  );

  // ── close / submenu coordination ───────────────────────────────────────
  const closeContextMenu = useCallback(() => {
    setContextMenuOpen(false);
    setReparentSubmenuOpen(false);
    setDeleteSubmenuOpen(false);
    setAddNewSubmenuOpen(false);
    setDuplicateSubmenuOpen(false);
    setAddJointKindSubmenuOpen(false);
    setAddGeomKindSubmenuOpen(false);
    setAddVisualGeomKindSubmenuOpen(false);
  }, []);

  const closeContextSubmenus = useCallback(() => {
    setReparentSubmenuOpen(false);
    setDeleteSubmenuOpen(false);
    setAddNewSubmenuOpen(false);
    setDuplicateSubmenuOpen(false);
    setAddJointKindSubmenuOpen(false);
    setAddGeomKindSubmenuOpen(false);
    setAddVisualGeomKindSubmenuOpen(false);
  }, []);

  // Body menu has four mutually exclusive top-level actions; this helper
  // flips exactly one open and folds the deeper Add-kind submenus when
  // we leave the Add branch.
  const setBodyActionSubmenuKey = useCallback((key) => {
    const nextDelete = key === "delete";
    const nextReparent = key === "reparent";
    const nextAdd = key === "addNew";
    const nextDuplicate = key === "duplicate";
    setDeleteSubmenuOpen(nextDelete);
    setReparentSubmenuOpen(nextReparent);
    setAddNewSubmenuOpen(nextAdd);
    setDuplicateSubmenuOpen(nextDuplicate);
    if (!nextAdd) {
      setAddJointKindSubmenuOpen(false);
      setAddGeomKindSubmenuOpen(false);
      setAddVisualGeomKindSubmenuOpen(false);
    }
  }, []);

  // Geom/site/inertial/mesh menus only expose the Reparent submenu.
  const setReparentOnlySubmenuKey = useCallback((key) => {
    setReparentSubmenuOpen(key === "reparent");
  }, []);

  // Joint menu has Reparent + AddNew but no Delete/Duplicate top-level keys.
  const setJointActionSubmenuKey = useCallback((key) => {
    setReparentSubmenuOpen(key === "reparent");
    setAddNewSubmenuOpen(key === "addNew");
  }, []);

  // ── openers ─────────────────────────────────────────────────────────────
  // All eight share the same shape: translate the event into layer-local
  // coords, clear every entity target, set the one we're targeting, set the
  // position, open the menu, and reset the submenus that don't belong to
  // this entity type. The shape is repeated rather than abstracted so each
  // call site stays trivially auditable.
  const positionFromEvent = useCallback(
    (event) => {
      const layerRect = interactionLayerRef.current?.getBoundingClientRect();
      const localX = layerRect ? event.clientX - layerRect.left : event.clientX;
      const localY = layerRect ? event.clientY - layerRect.top : event.clientY;
      return { x: localX, y: localY };
    },
    [interactionLayerRef]
  );

  const openContextMenuForBody = useCallback(
    (event, bodyUuid) => {
      event.preventDefault();
      event.stopPropagation();
      setContextMenuType("body");
      setContextMenuBodyUuid(bodyUuid || null);
      setContextMenuMeshId(null);
      setContextMenuGeomKey(null);
      setContextMenuSiteId(null);
      setContextMenuInertialKey(null);
      setContextMenuJointId(null);
      setContextMenuCameraId(null);
      setContextMenuPosition(positionFromEvent(event));
      setContextMenuOpen(true);
      setReparentSubmenuOpen(false);
      setDeleteSubmenuOpen(false);
      setDuplicateSubmenuOpen(false);
    },
    [positionFromEvent]
  );

  const openContextMenuForObject = useCallback(
    (event, rootBodyUuid) => {
      event.preventDefault();
      event.stopPropagation();
      setContextMenuType("object");
      setContextMenuBodyUuid(rootBodyUuid || null);
      setContextMenuMeshId(null);
      setContextMenuGeomKey(null);
      setContextMenuSiteId(null);
      setContextMenuInertialKey(null);
      setContextMenuJointId(null);
      setContextMenuCameraId(null);
      setContextMenuPosition(positionFromEvent(event));
      setContextMenuOpen(true);
      setReparentSubmenuOpen(false);
      setDeleteSubmenuOpen(false);
      setAddNewSubmenuOpen(false);
      setDuplicateSubmenuOpen(false);
    },
    [positionFromEvent]
  );

  const openContextMenuForEmptySpace = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setContextMenuType("canvas");
      setContextMenuBodyUuid(null);
      setContextMenuMeshId(null);
      setContextMenuGeomKey(null);
      setContextMenuSiteId(null);
      setContextMenuInertialKey(null);
      setContextMenuJointId(null);
      setContextMenuCameraId(null);
      setContextMenuPosition(positionFromEvent(event));
      setContextMenuOpen(true);
      setReparentSubmenuOpen(false);
      setDeleteSubmenuOpen(false);
      // Empty-space menu opens with the Add submenu pre-expanded for fast
      // access — except when physics is running (authoring is locked).
      setAddNewSubmenuOpen(!isPhysicsRunning);
      setDuplicateSubmenuOpen(false);
      setAddJointKindSubmenuOpen(false);
      setAddGeomKindSubmenuOpen(false);
      setAddVisualGeomKindSubmenuOpen(false);
    },
    [isPhysicsRunning, positionFromEvent]
  );

  const openContextMenuForGeom = useCallback(
    (event, geomKey) => {
      event.preventDefault();
      event.stopPropagation();
      setContextMenuType("geom");
      setContextMenuGeomKey(geomKey || null);
      setContextMenuBodyUuid(null);
      setContextMenuMeshId(null);
      setContextMenuSiteId(null);
      setContextMenuInertialKey(null);
      setContextMenuJointId(null);
      setContextMenuCameraId(null);
      setContextMenuPosition(positionFromEvent(event));
      setContextMenuOpen(true);
      setReparentSubmenuOpen(false);
      setDeleteSubmenuOpen(false);
      setDuplicateSubmenuOpen(false);
    },
    [positionFromEvent]
  );

  const openContextMenuForSite = useCallback(
    (event, siteId) => {
      event.preventDefault();
      event.stopPropagation();
      setContextMenuType("site");
      setContextMenuSiteId(siteId || null);
      setContextMenuBodyUuid(null);
      setContextMenuMeshId(null);
      setContextMenuGeomKey(null);
      setContextMenuInertialKey(null);
      setContextMenuJointId(null);
      setContextMenuCameraId(null);
      setContextMenuPosition(positionFromEvent(event));
      setContextMenuOpen(true);
      setReparentSubmenuOpen(false);
      setDeleteSubmenuOpen(false);
      setDuplicateSubmenuOpen(false);
    },
    [positionFromEvent]
  );

  const openContextMenuForInertial = useCallback(
    (event, inertialKey) => {
      event.preventDefault();
      event.stopPropagation();
      setContextMenuType("inertial");
      setContextMenuInertialKey(inertialKey || null);
      setContextMenuBodyUuid(null);
      setContextMenuMeshId(null);
      setContextMenuGeomKey(null);
      setContextMenuSiteId(null);
      setContextMenuJointId(null);
      setContextMenuCameraId(null);
      setContextMenuPosition(positionFromEvent(event));
      setContextMenuOpen(true);
      setReparentSubmenuOpen(false);
      setDeleteSubmenuOpen(false);
      setDuplicateSubmenuOpen(false);
    },
    [positionFromEvent]
  );

  const openContextMenuForJoint = useCallback(
    (event, jointId) => {
      event.preventDefault();
      event.stopPropagation();
      setContextMenuType("joint");
      setContextMenuJointId(jointId || null);
      setContextMenuBodyUuid(null);
      setContextMenuMeshId(null);
      setContextMenuGeomKey(null);
      setContextMenuSiteId(null);
      setContextMenuInertialKey(null);
      setContextMenuCameraId(null);
      setContextMenuPosition(positionFromEvent(event));
      setContextMenuOpen(true);
      setReparentSubmenuOpen(false);
      setDeleteSubmenuOpen(false);
      setDuplicateSubmenuOpen(false);
    },
    [positionFromEvent]
  );

  const openContextMenuForCamera = useCallback(
    (event, cameraUuid) => {
      event.preventDefault();
      event.stopPropagation();
      setContextMenuType("camera");
      setContextMenuCameraId(cameraUuid || null);
      setContextMenuBodyUuid(null);
      setContextMenuMeshId(null);
      setContextMenuGeomKey(null);
      setContextMenuSiteId(null);
      setContextMenuInertialKey(null);
      setContextMenuJointId(null);
      setContextMenuPosition(positionFromEvent(event));
      setContextMenuOpen(true);
      setReparentSubmenuOpen(false);
      setDeleteSubmenuOpen(false);
      setDuplicateSubmenuOpen(false);
    },
    [positionFromEvent]
  );

  // Used by the component's clearBatchSelection: wipes every menu target and
  // closes the menu without touching multi-selection state.
  const clearContextMenuTargets = useCallback(() => {
    setContextMenuBodyUuid(null);
    setContextMenuMeshId(null);
    setContextMenuGeomKey(null);
    setContextMenuSiteId(null);
    setContextMenuInertialKey(null);
    setContextMenuJointId(null);
    setContextMenuCameraId(null);
    setContextMenuOpen(false);
  }, []);

  // ── lifecycle effects ──────────────────────────────────────────────────
  // Outside-click and Escape close the menu while it is open.
  useEffect(() => {
    if (!contextMenuOpen) return;
    const onPointerDown = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        closeContextMenu();
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeContextMenu();
      }
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [contextMenuOpen, closeContextMenu]);

  // Mobile chrome (top/bottom edge bars) needs to know when an in-canvas
  // menu is open so it can collapse to give the menu more screen space.
  useSyncMobileEdgeChromeFromMenuOpen(contextMenuOpen);

  return {
    // primitive state
    contextMenuType,
    contextMenuBodyUuid,
    contextMenuMeshId,
    contextMenuGeomKey,
    contextMenuSiteId,
    contextMenuInertialKey,
    contextMenuJointId,
    contextMenuCameraId,
    contextMenuOpen,
    setContextMenuOpen,
    contextMenuPosition,
    reparentSubmenuOpen,
    deleteSubmenuOpen,
    addNewSubmenuOpen,
    setAddNewSubmenuOpen,
    duplicateSubmenuOpen,
    addJointKindSubmenuOpen,
    setAddJointKindSubmenuOpen,
    addGeomKindSubmenuOpen,
    setAddGeomKindSubmenuOpen,
    addVisualGeomKindSubmenuOpen,
    setAddVisualGeomKindSubmenuOpen,
    contextMenuRef,
    // derived target ids
    contextMenuBodyUuids,
    contextMenuBodyUuidSet,
    contextMenuMeshIds,
    contextMenuGeomKeys,
    contextMenuDirectCollisionGeomKey,
    contextMenuGeomSelectionLabel,
    contextMenuSiteIds,
    contextMenuInertialKeys,
    contextMenuInertialSourceBodyUuids,
    contextMenuJointIds,
    contextMenuCameraIds,
    // actions
    closeContextMenu,
    closeContextSubmenus,
    setBodyActionSubmenuKey,
    setReparentOnlySubmenuKey,
    setJointActionSubmenuKey,
    openContextMenuForBody,
    openContextMenuForObject,
    openContextMenuForEmptySpace,
    openContextMenuForGeom,
    openContextMenuForSite,
    openContextMenuForInertial,
    openContextMenuForJoint,
    openContextMenuForCamera,
    clearContextMenuTargets,
  };
}
